/**
 * MarkForReviewV2 - Mark for review toggle component
 *
 * Features:
 * - Toggle button with bookmark icon
 * - Visual feedback when marked
 * - Keyboard shortcut (M)
 */

import { memo, useCallback } from 'react'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'

// Bookmark icons
const BookmarkOutlineIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
    />
  </svg>
)

const BookmarkSolidIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M6.32 2.577a49.255 49.255 0 0111.36 0c1.497.174 2.57 1.46 2.57 2.93V21a.75.75 0 01-1.085.67L12 18.089l-7.165 3.583A.75.75 0 013.75 21V5.507c0-1.47 1.073-2.756 2.57-2.93z"
      clipRule="evenodd"
    />
  </svg>
)

const MarkForReviewV2 = memo(() => {
  // Store state
  const isMarkedForReview = useChallengeStoreV2((state) => state.isMarkedForReview)

  // Store actions
  const toggleMarkedForReview = useChallengeStoreV2((state) => state.toggleMarkedForReview)

  // Handle toggle
  const handleToggle = useCallback(() => {
    toggleMarkedForReview()
  }, [toggleMarkedForReview])

  return (
    <button
      onClick={handleToggle}
      className={`
        flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
        ${
          isMarkedForReview
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
        }
      `}
      title={isMarkedForReview ? 'Unmark for review (M)' : 'Mark for review (M)'}
      aria-pressed={isMarkedForReview}
    >
      {isMarkedForReview ? <BookmarkSolidIcon /> : <BookmarkOutlineIcon />}
      <span className="hidden sm:inline">{isMarkedForReview ? 'Marked' : 'Mark for Review'}</span>
      <span className="sm:hidden">{isMarkedForReview ? 'Marked' : 'Mark'}</span>
      <kbd className="hidden md:inline ml-1 px-1.5 py-0.5 text-xs bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
        M
      </kbd>
    </button>
  )
})

MarkForReviewV2.displayName = 'MarkForReviewV2'

export default MarkForReviewV2
